/**
 * Orchestrates the API calls for the Mapping workbench:
 *
 * 1. Fetch target columns (schema definition)
 * 2. Fetch known mappings
 * 3. Call processExtractionRegion (complex ingestion) or processFile fallback
 * 4. Return transformed MappingRow[] + schema lookups
 */

import { useState, useEffect } from "react";
import {
  fetchSchemaFields,
  fetchKnownMappings,
  processFile,
  processExtractionRegion,
} from "@/services/api";
import { buildSchemaLookups, type SchemaLookups } from "@/lib/schema";
import { resolveExtractionMappingTarget } from "@/lib/mappingRegions";
import { useUpload } from "@/hooks/useUpload";
import { useTelemetry } from "@/hooks/useTelemetry";
import type { MappingRow } from "@/types";

export interface ProcessingState {
  loading: boolean;
  error: string | null;
  rows: MappingRow[];
  sourceColumns: string[];
  duplicateColumns: string[];
  schema: SchemaLookups;
  processingTimeMs: number;
}

const EMPTY_SCHEMA = buildSchemaLookups([]);

export function useProcessing(): ProcessingState {
  const {
    file,
    schemaId,
    inputColumns,
    documentId,
    reportType,
    requiresReportType,
    isMultiRegion,
    activeRegion,
    currentRegionIndex,
    providerId,
    worksheets,
    mappingSelection,
  } = useUpload();
  const { track, trackError, startTimer } = useTelemetry();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [sourceColumns, setSourceColumns] = useState<string[]>([]);
  const [duplicateColumns, setDuplicateColumns] = useState<string[]>([]);
  const [schema, setSchema] = useState<SchemaLookups>(EMPTY_SCHEMA);
  const [processingTimeMs, setProcessingTimeMs] = useState(0);

  const regionReportType = isMultiRegion ? (activeRegion?.reportType ?? null) : reportType;
  const extractionTarget = resolveExtractionMappingTarget({
    isMultiRegion,
    activeRegionId: activeRegion?.regionId,
    activeRegionHeaders: activeRegion?.headers,
    worksheets,
    mappingSelection,
    inputColumns,
  });
  const extractionRegionId = extractionTarget?.regionId ?? null;
  const runKey = `${currentRegionIndex}:${regionReportType ?? ""}:${extractionRegionId ?? ""}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const stopTimer = startTimer();

    (async () => {
      try {
        const schemaFields = await fetchSchemaFields(
          schemaId ?? undefined,
          undefined,
          regionReportType ?? undefined
        );
        if (cancelled) return;
        const lookups = buildSchemaLookups(schemaFields);
        setSchema(lookups);

        track("processing_schema_loaded", { fieldCount: schemaFields.length });

        const knownMappings = await fetchKnownMappings(
          schemaId ?? undefined,
          providerId ?? undefined
        );
        if (cancelled) return;
        track("processing_known_mappings_loaded", { count: knownMappings.length });

        const effectiveFileId = documentId || crypto.randomUUID();
        const target = resolveExtractionMappingTarget({
          isMultiRegion,
          activeRegionId: activeRegion?.regionId,
          activeRegionHeaders: activeRegion?.headers,
          worksheets,
          mappingSelection,
          inputColumns,
        });

        // Complex ingestion: always map against extraction headers so
        // snapshot source_field matches mapped_data keys (single- and multi-region).
        if (target && documentId.trim() && schemaId) {
          if (requiresReportType && !regionReportType?.trim()) {
            throw new Error(
              isMultiRegion
                ? "Select a report type for this region before mapping."
                : "Select a report type before mapping."
            );
          }

          track("Processing.Initiated", {
            documentId: effectiveFileId,
            schemaId: schemaId || "unknown",
            fieldCount: schemaFields.length,
            knownMappingCount: knownMappings.length,
            reportType: regionReportType ?? undefined,
            regionId: target.regionId,
            multiRegion: isMultiRegion,
            extractionAligned: true,
          });

          const result = await processExtractionRegion({
            fileId: effectiveFileId,
            regionId: target.regionId,
            schemaId,
            reportType: regionReportType ?? null,
            sourceColumns: target.sourceColumns,
            schemaFields,
            knownMappings,
          });
          if (cancelled) return;

          setRows(result.rows);
          setSourceColumns(
            result.sourceColumns.length > 0 ? result.sourceColumns : target.sourceColumns
          );
          setDuplicateColumns(result.duplicateColumns);
          setProcessingTimeMs(result.processingTimeMs);
        } else {
          const effectiveFile = file ?? new File([""], "empty.csv");
          if (!file) {
            throw new Error("No file available to process. Please upload a file first.");
          }

          track("Processing.Initiated", {
            documentId: effectiveFileId,
            fileName: effectiveFile.name,
            schemaId: schemaId || "unknown",
            fieldCount: schemaFields.length,
            knownMappingCount: knownMappings.length,
            reportType: reportType ?? "unset",
            extractionAligned: false,
          });

          const result = await processFile(
            effectiveFile,
            effectiveFileId,
            schemaFields,
            knownMappings,
            reportType
          );
          if (cancelled) return;

          setRows(result.rows);
          setSourceColumns(result.sourceColumns.length > 0 ? result.sourceColumns : inputColumns);
          setDuplicateColumns(result.duplicateColumns);
          setProcessingTimeMs(result.processingTimeMs);
        }

        const durationMs = stopTimer();
        track("Processing.Succeeded", {
          documentId: effectiveFileId,
          mappingCount: rows.length,
          processingTimeMs,
          durationMs,
          multiRegion: isMultiRegion,
          regionId: target?.regionId ?? undefined,
          extractionAligned: Boolean(target && documentId.trim() && schemaId),
        });
      } catch (err) {
        if (cancelled) return;
        const durationMs = stopTimer();
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);

        track("Processing.Failed", {
          documentId: documentId || "unknown",
          errorMessage: msg,
          durationMs,
          multiRegion: isMultiRegion,
          regionId: extractionRegionId ?? undefined,
        });
        trackError(err, { context: "processing_pipeline" });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { loading, error, rows, sourceColumns, duplicateColumns, schema, processingTimeMs };
}
